
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { EPermissionActions } from '@/model/EPermissionActions';
import { UrlHelper } from '@/helpers/UrlHelper';

interface NavTab {
  route: string;
  icon: string;
  labelKey: string;
  label?: string;
  action?: string;
  actions?: string[];
}

@Component({ name: 'LockerNav' })
export default class LockerNav extends Vue {
  @Prop()
  protected lockerId!: number;

  private allTabs: NavTab[] = [
    { route: 'locker',                  icon: 'mdi-locker',                   labelKey: 'lockers.nav.details' },
    { route: 'lockerEdit',              icon: 'mdi-pencil',                   labelKey: 'lockers.nav.edit',             action: EPermissionActions.ACTION_LOCKERS_UPDATE },
    { route: 'installLocker',           icon: 'mdi-file-cabinet',             labelKey: 'lockers.nav.cells' },
    { route: 'lockersServiceProviders', icon: 'mdi-store',                    labelKey: 'lockers.nav.serviceProviders' },
    { route: 'lockersOrders',           icon: 'mdi-package-variant-closed',   labelKey: 'lockers.nav.orders' },
    { route: 'lockersScreensavers',     icon: 'mdi-image-multiple-outline',   labelKey: 'lockers.nav.screensavers',  action: EPermissionActions.ACTION_SCREENSAVERS },
    { route: 'lockersLandingpages',     icon: 'mdi-web',                      labelKey: 'lockers.nav.landingpages',     action: EPermissionActions.ACTION_LANDINGPAGES },
    { route: 'packagesToTransfer',      icon: 'mdi-transfer',                 labelKey: 'lockers.nav.transfer' },
    { route: 'lockersContracts',        icon: 'mdi-file-document-outline',    labelKey: 'lockers.nav.contracts',        actions: [EPermissionActions.ACTION_LOCKERS_FINANCES_VIEW, EPermissionActions.ACTION_FINANCES_UPDATE] },
  ];

  get visibleTabs(): NavTab[] {
    return this.allTabs
      .filter(tab => {
        if (tab.action && !this.$canAction(tab.action)) return false;
        if (tab.actions && !tab.actions.some(a => this.$canAction(a))) return false;
        return true;
      })
      .map(tab => ({
        ...tab,
        label: this.$t(tab.labelKey) as string,
      }));
  }

  tabRoute(tab: NavTab): any {
    return { name: tab.route, params: { lockerId: String(this.lockerId) } };
  }

  isActive(routeName: string): boolean {
    return this.$route.name === routeName;
  }

  mounted() {
    // The slider measures itself asynchronously (and again when its arrows appear), so re-adjust every time it does
    (this.$refs.group as any).$watch('widths', this.scrollActiveIntoView);
    this.scrollActiveIntoView();
  }

  @Watch('$route')
  onRouteChanged() {
    this.scrollActiveIntoView();
  }

  // v-chip-group can't scroll to its selected chip here: router-link chips toggle their own selection to
  // follow the router, so the group never keeps one. The active tab is brought into view by moving the
  // slider offset directly.
  private scrollActiveIntoView(): void {
    this.$nextTick(() => {
      const group: any = this.$refs.group;
      if (!group) return;
      const active = group.$el.querySelector('.locker-nav-active') as HTMLElement | null;
      if (!active || !group.isOverflowing) return;

      const visibleWidth = group.widths.wrapper;
      let offset = group.scrollOffset;
      if (active.offsetLeft < offset) {
        offset = active.offsetLeft;
      } else if (active.offsetLeft + active.offsetWidth > offset + visibleWidth) {
        offset = active.offsetLeft + active.offsetWidth - visibleWidth;
      }
      group.scrollOffset = Math.min(Math.max(offset, 0), Math.max(0, group.widths.content - visibleWidth));
    });
  }

  get oldBackofficeUrl(): string {
    return UrlHelper.mayorLocker(this.lockerId);
  }
}
